import { get, writable } from 'svelte/store'
import type { Report } from 'fixee-server'
import useFixeeClient from '../api/fixee-client'

const reports = writable<Report[]>([])
const joinedChannels = writable<Set<string>>(new Set())

const getReportsForPortals = async (portalIds: string[]) => {
  const { channelsToJoin, channelsToLeave } = subscribeToPortalIds(portalIds)

  reports.update(currentReports => {
    return [...currentReports.filter(r => !channelsToLeave.includes(r.portalId))]
  })

  if (!channelsToJoin.length) return

  const result = await useFixeeClient()
    .service('report')
    .find({
      query: {
        portalId: { $in: channelsToJoin },
      },
    })

  reports.update(currentReports => {
    return [...result.data, ...currentReports.filter(r => !channelsToJoin.includes(r.portalId))]
  })
}

const onPatched = async (report: Report) => {
  reports.update(currentReports => {
    return [report, ...currentReports.filter(r => r.id !== report.id)]
  })
}

const onCreated = async (report: Report) => {
  reports.update(currentReports => {
    return [report, ...currentReports]
  })
}

const onRemoved = async (report: Report) => {
  reports.update(currentReports => {
    return currentReports.filter(r => r.id !== report.id)
  })
}

const subscribeToPortalIds = (portalIds: string[]) => {
  const joinedChannelsValue = get(joinedChannels)
  const channelsToJoin = portalIds.filter(portalId => !joinedChannelsValue.has(portalId))
  const channelsToLeave = Array.from(joinedChannelsValue).filter(portalId => !portalIds.includes(portalId))

  if (channelsToLeave.length) {
    void useFixeeClient().service('report').leaveChannels({ portalIds: channelsToLeave })
  }

  if (channelsToJoin.length) {
    void useFixeeClient().service('report').joinChannels({ portalIds })
  }

  joinedChannels.set(new Set(portalIds))

  return { channelsToJoin, channelsToLeave }
}

const init = () => {
  useFixeeClient().service('report').on('patched', onPatched)
  useFixeeClient().service('report').on('created', onCreated)
  useFixeeClient().service('report').on('removed', onRemoved)

  subscribeToPortalIds(Array.from(get(joinedChannels)))
}

const deinit = () => {
  useFixeeClient().service('report').off('patched', onPatched)
  useFixeeClient().service('report').off('created', onCreated)
  useFixeeClient().service('report').off('removed', onRemoved)

  subscribeToPortalIds([])
}

export const reportsStore = {
  reports,
  getReportsForPortals,
  init,
  deinit,
}
